import React from 'react';
import TransactionItemWidget from '../TransactionItemWidget';
import UpcomingTransactionItemWidget from '../UpcomingTransactionItemWidget';
import { useTranslation } from 'react-i18next';

/**
 * Reusable component for rendering grouped transaction rows
 * Displays a date header followed by transaction items
 */
export const GroupedTransactionRow = ({ dateLabel, children }) => {
  return (
    <>
      <tr className="grouped-transaction-row">
        <td colSpan="7">
          {dateLabel}
        </td>
      </tr>
      {children}
    </>
  );
};

/**
 * Renders regular transactions grouped by date with pending items first
 * @param {Array} items - Array of transaction items
 * @param {Object} groupedData - Pre-grouped transaction data from groupTransactionsByDate utility
 * @param {Function} onCBKDataReceived - Callback for CBK transaction data
 * @param {Function} onCancelTransaction - Callback for canceling a transaction
 */
export const GroupedTransactionsList = ({ items, groupedData, onCBKDataReceived, onCancelTransaction }) => {
  const { t } = useTranslation();

  if (!items || items.length === 0) {
    return null;
  }

  const { pendingItems, groupedByDate } = groupedData;

  return (
    <>
      {/* Render pending transactions first */}
      {pendingItems.length > 0 && (
        <GroupedTransactionRow dateLabel={t('pending')}>
          {pendingItems.map((data) => (
            <TransactionItemWidget 
              key={data.id} 
              transactionData={data} 
              getCBKData={onCBKDataReceived}
              cancelTransaction = {onCancelTransaction}
            />
          ))}
        </GroupedTransactionRow>
      )}

      {/* Render non-pending transactions grouped by date */}
      {Object.entries(groupedByDate).map(([date, group]) => (
        <GroupedTransactionRow key={date} dateLabel={date}>
          {group.map((data) => (
            <TransactionItemWidget 
              key={data.id} 
              transactionData={data} 
              getCBKData={onCBKDataReceived} 
              cancelTransaction = {onCancelTransaction}
            />
          ))}
        </GroupedTransactionRow>
      ))}
    </>
  );
};

/**
 * Renders upcoming transactions grouped by start date
 * @param {Array} items - Array of upcoming transaction items
 * @param {Object} groupedData - Pre-grouped transaction data from groupUpcomingTransactionsByDate utility
 * @param {Function} onCancelTransaction - Callback for canceling a transaction
 */
export const GroupedUpcomingTransactionsList = ({ items, groupedData, onCancelTransaction }) => {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <>
      {Object.entries(groupedData).map(([date, group]) => (
        <GroupedTransactionRow key={date} dateLabel={date}>
          {group.map((data) => (
            <UpcomingTransactionItemWidget 
              key={data.TransactionId} 
              cancelUpcomingTransaction={onCancelTransaction} 
              transactionData={data} 
            />
          ))}
        </GroupedTransactionRow>
      ))}
    </>
  );
};

export default GroupedTransactionsList;
